import { type TypedDocumentNode, gql } from '@apollo/client';
import { faStars } from '@fortawesome/pro-solid-svg-icons';
import styled, { css } from 'styled-components';

import {
  InGameCurrency,
  Rarity,
} from '@sorare/core/src/__generated__/globalTypes';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { Energy } from '@sorare/core/src/atoms/icons/Energy';
import { ScarcityIcon } from '@sorare/core/src/atoms/icons/ScarcityIcon';
import { getRewardImage } from '@sorare/core/src/lib/rewards';

import type { AnyRewardConfigImage_anyRewardConfigInterface } from './__generated__/index.graphql';

const StyledImg = styled.img`
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: contain;
`;
const forcedComponentStyle = css`
  width: 90% !important;
  height: unset !important;
  aspect-ratio: 1/1;
`;
const StyledCard = styled(ScarcityIcon)`
  ${forcedComponentStyle}
`;

type Props = {
  anyRewardConfig: AnyRewardConfigImage_anyRewardConfigInterface;
  rarity?: Rarity;
};

export const AnyRewardConfigImage = ({ anyRewardConfig, rarity }: Props) => {
  switch (anyRewardConfig.__typename) {
    case 'CardRewardConfig':
      return <StyledCard rarity={rarity || Rarity.common} />;
    case 'ExperienceRewardConfig':
      return anyRewardConfig.pictureUrl ? (
        <StyledImg src={anyRewardConfig.pictureUrl} alt="" />
      ) : (
        <FontAwesomeIcon icon={faStars} />
      );
    case 'InGameCurrencyRewardConfig':
      switch (anyRewardConfig.currency) {
        case InGameCurrency.LIMITED_ENERGY:
          return <Energy variant={Rarity.limited} />;
        case InGameCurrency.RARE_ENERGY:
          return <Energy variant={Rarity.rare} />;
        case InGameCurrency.SUPER_RARE_ENERGY:
          return <Energy variant={Rarity.super_rare} />;
        case InGameCurrency.UNIQUE_ENERGY:
          return <Energy variant={Rarity.unique} />;
        default:
          return <StyledImg src={getRewardImage(anyRewardConfig)} alt="" />;
      }
    default:
      return <StyledImg src={getRewardImage(anyRewardConfig)} alt="" />;
  }
};

AnyRewardConfigImage.fragments = {
  anyRewardConfigInterface: gql`
    fragment AnyRewardConfigImage_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ... on InGameCurrencyRewardConfig {
        id
        currency
      }
      ... on CardRewardConfig {
        id
        rarity
      }
      ... on ExperienceRewardConfig {
        id
        pictureUrl
      }
      ...getRewardImage_anyRewardConfigInterface
    }
    ${getRewardImage.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<AnyRewardConfigImage_anyRewardConfigInterface>,
};
